import { Photo } from '../types';

// Reconnect backoff: start fast (1s) so a brief drop barely delays new photos,
// then back off exponentially to avoid hammering a down server.
const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 10_000;
function reconnectDelay(attempt: number): number {
  return Math.min(RECONNECT_BASE_MS * 2 ** attempt, RECONNECT_MAX_MS);
}

// ── Gallery SSE ───────────────────────────────────────────────────────────────

/** Payload for `photo.ready` — the original finished compressing and is openable. */
export interface GalleryPhotoReady {
  photoId: string;
  originalUrl: string;
}

interface EventPhotosStreamHandlers {
  onPhotoCreated: (photo: Photo) => void;
  /** Fired when a previously-"preparing" photo's original becomes available. */
  onPhotoReady?: (update: GalleryPhotoReady) => void;
  onConnected?: () => void;
  onDisconnected?: () => void;
}

/**
 * Subscribe to the real-time SSE stream for an event's gallery.
 *
 * Sends `photo.created` events when a new photo arrives (FTP or manual upload).
 * Falls back gracefully — on disconnect, auto-reconnects after 3s.
 *
 * Returns a cleanup function — call it on component unmount.
 */
export function subscribeToEventPhotos(
  eventId: string,
  handlers: EventPhotosStreamHandlers,
): () => void {
  let es: EventSource | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;
  let attempt = 0;

  function connect() {
    if (stopped) return;

    es = new EventSource(`/api/v1/events/${eventId}/photos/stream`, { withCredentials: true });

    es.addEventListener('connected', () => {
      attempt = 0; // healthy connection — reset backoff
      handlers.onConnected?.();
    });

    es.addEventListener('photo.created', (e: MessageEvent) => {
      try {
        const payload = JSON.parse(e.data) as { photo: Photo };
        if (payload?.photo) {
          handlers.onPhotoCreated(payload.photo);
        }
      } catch {
        // malformed payload — ignore
      }
    });

    es.addEventListener('photo.ready', (e: MessageEvent) => {
      try {
        const payload = JSON.parse(e.data) as GalleryPhotoReady;
        if (payload?.photoId) {
          handlers.onPhotoReady?.(payload);
        }
      } catch {
        // malformed payload — ignore
      }
    });

    es.addEventListener('ping', () => {});

    es.onerror = () => {
      es?.close();
      es = null;
      handlers.onDisconnected?.();
      if (!stopped) {
        reconnectTimer = setTimeout(connect, reconnectDelay(attempt++));
      }
    };
  }

  connect();

  return () => {
    stopped = true;
    if (reconnectTimer) clearTimeout(reconnectTimer);
    es?.close();
    es = null;
  };
}

// ── Album SSE ─────────────────────────────────────────────────────────────────

/** Payload for `photo.updated` — overlay compositing finished (or failed). */
export interface AlbumPhotoUpdate {
  photoId: string;
  processedStatus: 'processed' | 'failed';
  processedThumbnailUrl?: string;
  processedUrl?: string | null;
}

interface AlbumStreamHandlers {
  /** Called when a new photo is fanned out to the live album. */
  onPhotoAdded: (photo: Photo) => void;
  /** Called when overlay compositing finishes — swap in the overlaid image. */
  onPhotoUpdated?: (update: AlbumPhotoUpdate) => void;
  /** Called when the SSE connection is established or re-established. */
  onConnected?: () => void;
  /** Called when the connection is lost (before auto-reconnect). */
  onDisconnected?: () => void;
}

/**
 * Subscribe to the real-time SSE stream for a live album.
 *
 * Automatically reconnects on error with an exponential backoff (1s → 10s).
 * Returns a cleanup function — call it on component unmount.
 *
 * @example
 * useEffect(() => {
 *   if (!album?.isLive) return;
 *   return subscribeToAlbumStream(albumId, { onPhotoAdded: (p) => prepend(p) });
 * }, [albumId, album?.isLive]);
 */
export function subscribeToAlbumStream(
  albumId: string,
  handlers: AlbumStreamHandlers,
): () => void {
  let es: EventSource | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;
  let attempt = 0;

  function connect() {
    if (stopped) return;

    es = new EventSource(`/api/v1/albums/${albumId}/stream`, { withCredentials: true });

    es.addEventListener('connected', () => {
      attempt = 0; // healthy connection — reset backoff
      handlers.onConnected?.();
    });

    es.addEventListener('photo.added', (e: MessageEvent) => {
      try {
        const payload = JSON.parse(e.data) as { photo: Photo };
        if (payload?.photo) {
          handlers.onPhotoAdded(payload.photo);
        }
      } catch {
        // malformed SSE payload — ignore
      }
    });

    es.addEventListener('photo.updated', (e: MessageEvent) => {
      try {
        const payload = JSON.parse(e.data) as AlbumPhotoUpdate;
        if (payload?.photoId) {
          handlers.onPhotoUpdated?.(payload);
        }
      } catch {
        // malformed SSE payload — ignore
      }
    });

    // Keep-alive — no action needed
    es.addEventListener('ping', () => {});

    es.onerror = () => {
      es?.close();
      es = null;
      handlers.onDisconnected?.();
      if (!stopped) {
        reconnectTimer = setTimeout(connect, reconnectDelay(attempt++));
      }
    };
  }

  connect();

  return () => {
    stopped = true;
    if (reconnectTimer) clearTimeout(reconnectTimer);
    es?.close();
    es = null;
  };
}
